<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'RentalService',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    oid: {
      type: String,
      required: false,
      default: null,
    },
    rental: {
      type: Object,
      required: false,
      default: null,
    },
    meter: {
      type: Object,
      required: false,
      default: null,
    },
    preventEditRental: {
      type: Boolean,
      required: false,
      default: false,
    },
    preventEditMeter: {
      type: Boolean,
      required: false,
      default: false,
    },
    assignToRental: {
      type: Boolean,
      required: false,
      default: false,
    },
    assignToMeter: {
      type: Boolean,
      required: false,
      default: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const item = ref(null);
  const units = ref(null);
  const formName = ref('rentalService');
  const formId = ref(null);
  const labelAlign = ref('right');
  const isReadOnly = ref(null);
  const tariffs = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'md',
  }));
  const b_2070266589801014_dataSource = computed(() => ({
    name: 'suggest_meter',
  }));
  const b_5074589820235598_dataSource = computed(() => ({
    name: 'suggest_product',
    parameters: {
      product_types: 'service',
    },
  }));

  const $data = computed(() => ({
    item: item.value,
    units: units.value,
    formName: formName.value,
    formId: formId.value,
    labelAlign: labelAlign.value,
    isReadOnly: isReadOnly.value,
    tariffs: tariffs.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_2070266589801014_dataSource: b_2070266589801014_dataSource.value,
    b_5074589820235598_dataSource: b_5074589820235598_dataSource.value,
  }));

  const $$load_item = () => {
    return props.oid
      ? $fn.value.fetch('read_rental_service', {
          parameters: { id: props.oid },
        })
      : $fn.value.fetch('get_rental_service_defaults', {
          parameters: {
            meter_id: props.meter ? props.meter.id : null,
            rental_id: props.rental ? props.rental.id : null,
          },
        });
  };
  const $$load_units = () => {
    return $fn.value
      .fetch('list_unit')
      .then((d) => _.orderBy(d, [(p) => p.symbol.toLowerCase()]));
  };
  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };
  const $$load_isReadOnly = () => {
    return item.value.locked;
  };
  const $$load_tariffs = () => {
    return $app.value.repo.get('list_object_by_type', {
      parameters: { object_type: 'tariff' },
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'item' },
      { name: 'units' },
      { name: 'formName' },
      { name: 'formId' },
      { name: 'labelAlign' },
      { name: 'isReadOnly', dependsOn: ['item'] },
      { name: 'tariffs' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_2070266589801014_dataSource', nodeId: '2070266589801014' },
      { name: 'b_5074589820235598_dataSource', nodeId: '5074589820235598' },
    ],
    loaders: {
      item: $$load_item,
      units: $$load_units,
      formId: $$load_formId,
      isReadOnly: $$load_isReadOnly,
      tariffs: $$load_tariffs,
    },
    ux: {},
    dataRefs: {
      item,
      units,
      formName,
      formId,
      labelAlign,
      isReadOnly,
      tariffs,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_2070266589801014_dataSource,
      b_5074589820235598_dataSource,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: props.oid
        ? $fn.value.gettext('Edit Rental Service')
        : $fn.value.gettext('Add Rental Service'),
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.oid,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('oid');
    }
  );
  watch(
    () => props.rental,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('rental');
    }
  );
  watch(
    () => props.meter,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('meter');
    }
  );
  watch(
    () => props.preventEditRental,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('preventEditRental');
    }
  );
  watch(
    () => props.preventEditMeter,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('preventEditMeter');
    }
  );
  watch(
    () => props.assignToRental,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('assignToRental');
    }
  );
  watch(
    () => props.assignToMeter,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('assignToMeter');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_qvatb93no_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeModal_6gpa401ls_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        ok: true,
        value: $event.data.response.data,
      },
    };
  };
  const act_1040167445267876_request_dn6twnbdv_getActionArgs = async (
    $event
  ) => {
    return {
      operation: item.value.id
        ? 'update_rental_service'
        : 'create_rental_service',
      data: _.extend(item.value, { rental_id: item.value.rental.id }),
    };
  };
  const act_2070266589801014_script_tfs6mq8rx_value_callable = async (
    $event
  ) => {
    return _.extend(item.value, $event.data.response.data);
  };
  const act_2070266589801014_script_tfs6mq8rx_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_2070266589801014_script_tfs6mq8rx_value_callable($event);
      },
    };
  };
  const act_2070266589801014_request_0nfbl8d06_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'get_rental_service_defaults',
      data: {
        rental_id: props.rental ? props.rental.id : item.value.rental_id,
        meter_id: item.value.meter.id,
      },
    };
  };
  const act_5074589820235598_setData_yr490f4jm_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item.service_id',
      value: item.value.service.id,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_3322283983417540_closeModal_ksbr2qyy8_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
      },
    };
  };
  const act_5419443714917073_closeModal_576sckg3z_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
      },
    };
  };
  const act_2167826738365259_closeModal_or2fv8ovh_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        cancel: true,
      },
    };
  };

  const act_7315092382398562_closeModal_qvatb93no =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_qvatb93no_getActionArgs,
    });
  const inst_evh_7315092382398562_close_2d3vccopg =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_qvatb93no,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_2d3vccopg],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_mjfyswxdc =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_eoehjbs5y =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_mjfyswxdc,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_eoehjbs5y],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_zk257gsm2 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_u6mul6xjr =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_zk257gsm2,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_u6mul6xjr],
    event: 'close',
  });
  const act_1040167445267876_closeModal_6gpa401ls =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_6gpa401ls_getActionArgs,
    });
  const inst_evh_1040167445267876_success_m5lh9ldyb =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_6gpa401ls,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_1040167445267876_request_dn6twnbdv = new actionTypes.RequestAction({
    actionArgs: act_1040167445267876_request_dn6twnbdv_getActionArgs,
    displayName: 'submit',
    events: [inst_evh_1040167445267876_success_m5lh9ldyb],
  });
  const inst_evh_1040167445267876_submit_11f6qhn4o =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_request_dn6twnbdv,
      event: 'submit',
      displayName: 'submit',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_11f6qhn4o],
    event: 'submit',
  });
  const act_2070266589801014_script_tfs6mq8rx = new actionTypes.ScriptAction({
    actionArgs: act_2070266589801014_script_tfs6mq8rx_getActionArgs,
  });
  const inst_evh_2070266589801014_success_93l77ia5h =
    new actionTypes.EventHandlerImpl({
      action: act_2070266589801014_script_tfs6mq8rx,
      event: 'success',
      displayName: 'script',
    });
  const act_2070266589801014_request_0nfbl8d06 = new actionTypes.RequestAction({
    actionArgs: act_2070266589801014_request_0nfbl8d06_getActionArgs,
    displayName: 'getDefaultsOnMeterSelected',
    events: [inst_evh_2070266589801014_success_93l77ia5h],
  });
  const inst_evh_2070266589801014_optionSelected_ktdlexxsr =
    new actionTypes.EventHandlerImpl({
      action: act_2070266589801014_request_0nfbl8d06,
      event: 'optionSelected',
      displayName: 'getDefaultsOnMeterSelected',
    });
  const inst_evh_2070266589801014_optionSelected =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2070266589801014_optionSelected_ktdlexxsr],
      event: 'optionSelected',
    });
  const act_5074589820235598_setData_yr490f4jm = new actionTypes.SetDataAction({
    actionArgs: act_5074589820235598_setData_yr490f4jm_getActionArgs,
  });
  const inst_evh_5074589820235598_optionSelected_7av7kefo7 =
    new actionTypes.EventHandlerImpl({
      action: act_5074589820235598_setData_yr490f4jm,
      event: 'optionSelected',
      displayName: 'setData',
    });
  const inst_evh_5074589820235598_optionSelected =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5074589820235598_optionSelected_7av7kefo7],
      event: 'optionSelected',
    });
  const act_3322283983417540_closeModal_ksbr2qyy8 =
    new actionTypes.CloseModalAction({
      actionArgs: act_3322283983417540_closeModal_ksbr2qyy8_getActionArgs,
    });
  const inst_evh_3322283983417540_success_9qsagdftl =
    new actionTypes.EventHandlerImpl({
      action: act_3322283983417540_closeModal_ksbr2qyy8,
      event: 'success',
      displayName: 'closeModal',
    });
  const inst_evh_3322283983417540_success = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_3322283983417540_success_9qsagdftl],
    event: 'success',
  });
  const act_5419443714917073_closeModal_576sckg3z =
    new actionTypes.CloseModalAction({
      actionArgs: act_5419443714917073_closeModal_576sckg3z_getActionArgs,
    });
  const inst_evh_5419443714917073_success_2ijqb91v9 =
    new actionTypes.EventHandlerImpl({
      action: act_5419443714917073_closeModal_576sckg3z,
      event: 'success',
      displayName: 'closeModal',
    });
  const inst_evh_5419443714917073_success = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5419443714917073_success_2ijqb91v9],
    event: 'success',
  });
  const act_2167826738365259_closeModal_or2fv8ovh =
    new actionTypes.CloseModalAction({
      actionArgs: act_2167826738365259_closeModal_or2fv8ovh_getActionArgs,
    });
  const inst_evh_2167826738365259_clickPreventStop_9fd65i8b7 =
    new actionTypes.EventHandlerImpl({
      action: act_2167826738365259_closeModal_or2fv8ovh,
      event: 'click.prevent.stop',
      displayName: 'closeModal',
    });
  const inst_evh_2167826738365259_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2167826738365259_clickPreventStop_9fd65i8b7],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_2d3vccopg,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_eoehjbs5y,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_u6mul6xjr,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_m5lh9ldyb,
    inst_evh_1040167445267876_submit_11f6qhn4o,
    submit: inst_evh_1040167445267876_submit_11f6qhn4o,
    inst_evh_1040167445267876_submit,
    inst_evh_2070266589801014_success_93l77ia5h,
    inst_evh_2070266589801014_optionSelected_ktdlexxsr,
    getDefaultsOnMeterSelected:
      inst_evh_2070266589801014_optionSelected_ktdlexxsr,
    inst_evh_2070266589801014_optionSelected,
    inst_evh_5074589820235598_optionSelected_7av7kefo7,
    inst_evh_5074589820235598_optionSelected,
    inst_evh_3322283983417540_success_9qsagdftl,
    inst_evh_3322283983417540_success,
    inst_evh_5419443714917073_success_2ijqb91v9,
    inst_evh_5419443714917073_success,
    inst_evh_2167826738365259_clickPreventStop_9fd65i8b7,
    inst_evh_2167826738365259_clickPreventStop,
  };

  const evh_7315092382398562_close_2d3vccopg = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_2d3vccopg.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_eoehjbs5y = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_eoehjbs5y.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_u6mul6xjr = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_u6mul6xjr.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_m5lh9ldyb = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_m5lh9ldyb.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_11f6qhn4o = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_11f6qhn4o.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2070266589801014_success_93l77ia5h = async (event, scope) => {
    return await actions.inst_evh_2070266589801014_success_93l77ia5h.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2070266589801014_optionSelected_ktdlexxsr = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2070266589801014_optionSelected_ktdlexxsr.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2070266589801014_optionSelected = async (event, scope) => {
    return await actions.inst_evh_2070266589801014_optionSelected.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5074589820235598_optionSelected_7av7kefo7 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5074589820235598_optionSelected_7av7kefo7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5074589820235598_optionSelected = async (event, scope) => {
    return await actions.inst_evh_5074589820235598_optionSelected.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3322283983417540_success_9qsagdftl = async (event, scope) => {
    return await actions.inst_evh_3322283983417540_success_9qsagdftl.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3322283983417540_success = async (event, scope) => {
    return await actions.inst_evh_3322283983417540_success.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5419443714917073_success_2ijqb91v9 = async (event, scope) => {
    return await actions.inst_evh_5419443714917073_success_2ijqb91v9.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5419443714917073_success = async (event, scope) => {
    return await actions.inst_evh_5419443714917073_success.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2167826738365259_clickPreventStop_9fd65i8b7 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2167826738365259_clickPreventStop_9fd65i8b7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2167826738365259_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2167826738365259_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="RentalService"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :name="formName"
        :schema="oid == null ? 'CreateRentalService' : 'UpdateRentalService'"
        :id="formId"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <div v-if="assignToRental">
          <ItpFormGroup
            :label-cols-md="4"
            :content-cols-md="8"
            :labelAlign="labelAlign"
            name="rental"
            :label="pgettext('RentalService', 'Rental')"
            required
          >
            <UcRentalSelector
              name="rental"
              :item="item"
              path="rental"
              :readonly="isReadOnly || preventEditRental"
              required
            >
            </UcRentalSelector>
          </ItpFormGroup>
          <hr />
        </div>
        <ItpFormGroup
          name="meter"
          :label="pgettext('RentalService', 'Meter')"
          :label-cols-md="4"
          :content-cols-md="8"
          :labelAlign="labelAlign"
          required
        >
          <ItpFormSearchableSelect
            name="meter"
            v-model="item.meter"
            :dataSource="b_2070266589801014_dataSource"
            :initialOptions="item.meter ? [item.meter] : []"
            valueField="id"
            textField="serial_number"
            bind-object
            use-option-template-for-selected-option
            optionTemplate='<UcSuggestMeterOption :option="option"></UcSuggestMeterOption>'
            required
            :readonly="preventEditMeter || isReadOnly"
            @optionSelected="evh_2070266589801014_optionSelected($event, {})"
          >
          </ItpFormSearchableSelect>
        </ItpFormGroup>
        <ItpFormGroup
          name="meter_factor"
          :label="pgettext('RentalService', 'Meter factor')"
          :label-cols-md="4"
          :content-cols-md="4"
          :labelAlign="labelAlign"
          required
        >
          <ItpFormInput
            type="number"
            name="meter_factor"
            v-model="item.meter_factor"
            :readonly="isReadOnly"
            required
          >
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup
          name="service"
          :label="pgettext('RentalService', 'Service')"
          :label-cols-md="4"
          :content-cols-md="8"
          :labelAlign="labelAlign"
          required
        >
          <ItpFormSearchableSelect
            name="service"
            v-model="item.service"
            :dataSource="b_5074589820235598_dataSource"
            bind-object
            valueField="id"
            textField="name"
            :readonly="isReadOnly"
            required
            @optionSelected="evh_5074589820235598_optionSelected($event, {})"
          >
          </ItpFormSearchableSelect>
        </ItpFormGroup>
        <ItpFormGroup
          name="tariff_code"
          :label="pgettext('RentalService', 'Tariff')"
          :label-cols-md="4"
          :content-cols-md="8"
          :labelAlign="labelAlign"
          v-if="tariffs && tariffs.length"
        >
          <ItpFormSearchableSelect
            name="tariff_code"
            v-model="item.tariff_code"
            :options="tariffs"
            clearable
            valueField="code"
            textField="code,name"
          >
          </ItpFormSearchableSelect>
        </ItpFormGroup>
        <ItpFormGroup
          name="billing_unit"
          :label="pgettext('RentalService', 'Billing unit')"
          :label-cols-md="4"
          :content-cols-md="3"
          :labelAlign="labelAlign"
          required
        >
          <ItpFormSearchableSelect
            name="billing_unit"
            v-model="item.billing_unit"
            :options="units"
            valueField="symbol"
            textField="symbol"
            :readonly="isReadOnly"
            required
          >
          </ItpFormSearchableSelect>
        </ItpFormGroup>
        <ItpFormGroup
          name="non_billable"
          :label="pgettext('RentalService', 'Non billable')"
          :label-cols-md="4"
          :content-cols-md="8"
          :labelAlign="labelAlign"
        >
          <ItpFormCheckbox
            name="non_billable"
            inline
            v-model="item.non_billable"
            :readonly="isReadOnly"
          >
          </ItpFormCheckbox>
        </ItpFormGroup>
        <hr />
        <ItpFormGroup
          name="valid_from"
          :label="pgettext('RentalService', 'Validity')"
          :label-cols-md="4"
          :content-cols-md="8"
          :labelAlign="labelAlign"
          contentClass="row"
        >
          <div class="col">
            <ItpFormDatetime
              type="date"
              name="valid_from"
              :placeholder="pgettext('InputPlaceholder', 'Valid from')"
              v-model="item.valid_from"
              :readonly="isReadOnly"
            >
            </ItpFormDatetime>
          </div>
          <div class="col">
            <ItpFormDatetime
              type="date"
              name="valid_to"
              :placeholder="pgettext('InputPlaceholder', 'Valid to')"
              v-model="item.valid_to"
              :readonly="isReadOnly"
            >
            </ItpFormDatetime>
          </div>
        </ItpFormGroup>
        <ItpFormGroup
          name="inactive"
          :label="pgettext('RentalService', 'Inactive')"
          :label-cols-md="4"
          :content-cols-md="8"
          :labelAlign="labelAlign"
        >
          <ItpFormCheckbox
            name="inactive"
            inline
            v-model="item.inactive"
            :readonly="isReadOnly"
          >
          </ItpFormCheckbox>
        </ItpFormGroup>
        <hr />
        <ItpFormGroup
          name="notes"
          :label="pgettext('RentalService', 'Notes')"
          :label-cols-md="4"
          :content-cols-md="8"
          :labelAlign="labelAlign"
        >
          <ItpFormTextarea
            name="note"
            v-model="item.notes"
            :readonly="isReadOnly"
          >
          </ItpFormTextarea>
        </ItpFormGroup>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpBox class="me-auto" v-if="oid && !item.locked">
          <BtnDeleteResource
            :resource="item"
            :disabled="actions.submit.isRunning"
            @success="evh_3322283983417540_success($event, {})"
          >
          </BtnDeleteResource>
          <BtnLockResource
            :resource="item"
            :disabled="actions.submit.isRunning"
            class="ms-3"
            @success="evh_5419443714917073_success($event, {})"
          >
          </BtnLockResource>
        </ItpBox>
        <ItpButton
          :text="pgettext('ModalButton', 'Save')"
          type="submit"
          :form="formId"
          variant="primary"
          icon="fa-save"
          :spinning="actions.submit.isRunning"
          :disabled="!forms[formName] || !forms[formName].submittable"
          v-if="!item.locked"
        >
        </ItpButton>
        <ItpButton
          :text="pgettext('ModalButton', 'Close')"
          variant="default"
          :disabled="actions.submit.isRunning"
          @click.prevent.stop="
            evh_2167826738365259_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
